import axios from 'axios';

// Base URLs
const GEOLOCATION_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API_URL = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API_URL, {
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (response.data && response.data.isSuccess) {
      return {
        success: true,
        data: response.data.data,
        fullResponse: response.data
      };
    }
    
    return {
      success: false,
      data: null,
      fullResponse: null
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      data: null,
      fullResponse: null,
      error: error.message
    };
  }
};

/**
 * Submit lead/form data
 * @param {Object} formData - Form data to submit
 * @param {Object} geolocationData - Geolocation data (optional)
 * @returns {Promise<Object>} API response with status
 */
export const submitLead = async (formData, geolocationData = null) => {
  try {
    // Extract only numeric phone number (remove any formatting)
    const numericPhone = formData.phoneNumber.replace(/\D/g, '');
    
    // Get current page URL
    const pageUrl = window.location.href;
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryDialCode) || 1,
      country: formData.country || 'US',
      phoneNumber: numericPhone,
      ipAddress: geolocationData?.ipAddress || '',
      description: formData.message || formData.description || '',
      pageUrl: pageUrl,
      city: geolocationData?.cityName || '',
      state: geolocationData?.regionName || '',
      geoLocation: geolocationData ? JSON.stringify({
        isSuccess: true,
        data: geolocationData,
        errorMessage: '',
        statusCode: 200
      }) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    const response = await axios.post(LEAD_API_URL, payload, {
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      }
    });

    // Handle 200 success response
    if (response.status === 200 && response.data?.isSuccess) {
      return {
        success: true,
        customerId: response.data.data?.customerId,
        message: response.data.data?.message || 'Lead created successfully',
        statusCode: 200,
        shouldRedirect: true
      };
    }

    // Handle other success cases
    return {
      success: true,
      data: response.data,
      statusCode: response.status
    };

  } catch (error) {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      return {
        success: false,
        statusCode: 401,
        message: error.response.data?.message || 'Unauthorized access',
        error: error.response.data?.message
      };
    }

    // Handle 400 Bad Request
    if (error.response?.status === 400) {
      return {
        success: false,
        statusCode: 400,
        message: error.response.data?.errorMessage || error.response.data?.data?.message || 'Invalid data',
        error: error.response.data?.errorMessage || error.response.data?.data?.message
      };
    }

    // Handle other errors
    return {
      success: false,
      statusCode: error.response?.status || 500,
      message: error.response?.data?.errorMessage || error.response?.data?.message || error.message,
      error: error.message
    };
  }
};

/**
 * Parse phone number to extract country code and number
 * @param {string} fullPhoneNumber - Full phone number with country code
 * @returns {Object} Parsed phone data
 */
export const parsePhoneNumber = (fullPhoneNumber) => {
  // Remove all non-numeric characters
  const cleaned = fullPhoneNumber.replace(/\D/g, '');
  
  // Extract country code (assuming first 1-3 digits)
  // This is a simple implementation, you might want to use a library like libphonenumber-js for production
  let countryDialCode = '1'; // Default to US
  let phoneNumber = cleaned;
  
  if (cleaned.length > 10) {
    // Assume first 1-2 digits are country code
    countryDialCode = cleaned.substring(0, cleaned.length - 10);
    phoneNumber = cleaned.substring(cleaned.length - 10);
  }
  
  return {
    countryDialCode: parseInt(countryDialCode),
    phoneNumber: phoneNumber
  };
};

export default {
  fetchGeolocation,
  submitLead,
  parsePhoneNumber
};
